/**
 * BOSS Reports — the pivot grid's right-click menu (gap analysis ranks 2, 3, 4: C4, C5, C7, C8, S3).
 *
 * The legacy grid replaced the stock menu on a row/column value with exactly: Expand All,
 * Collapse All, Show Totals ▸, Sort By ▸, Group By ▸ and Format To ▸ (`MainForm.vb:2490-2506`).
 * Only the ones that can actually be backed are here — Group By (rank 8) is out of scope — because
 * a menu item that does nothing is worse than a missing one. Show Totals (C6) had no route through
 * the engine, and got one when the side-by-side grid started drawing its own subtotal rows
 * (lib/bossRowCollapse): it is offered on every row field there but the last, whose groups are
 * single rows with nothing to total, collapsed or not. Show Grand Total (C12, a Report-menu switch
 * on the desktop) sits beside it on every row field, the last included.
 *
 * Row Layout is the one item with no legacy counterpart, and it exists for the same reason the
 * others were dropped: the legacy grid had no such choice because it only ever drew row fields
 * side by side, whereas Perspective can draw either. Without the item, whichever shape a report
 * opened in would be the only one it could ever have.
 *
 * `components/ContextMenu.tsx` already solves portal rendering, four-edge viewport clamping and
 * dismissal on outside-click/Escape, but it wraps a trigger element and opens where it was
 * clicked. This menu is handed a coordinate by the grid's hit test, so it borrows that component's
 * approach rather than the component itself. A context menu is not a dimming modal: it SHOULD
 * close on an outside click, and does.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, JSX, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'

import type { BossFormat, BossRowLayout } from '../../lib/bossLayoutOps'
// Type-only, and it must stay that way: PivotGrid pulls in ~3.8 MB of Perspective WASM behind the
// page's React.lazy boundary, and a value import here would drag all of it onto the route before
// a report has been run. `import type` is erased entirely at build time.
import type { BossGridTarget } from './PivotGrid'

import '../../styles/boss-gridmenu.css'

export type GridMenuProps = {
    target: BossGridTarget
    position: { x: number; y: number }
    /** Measures currently in the Values area, for Sort By. */
    measures: string[]
    /** True when group_by is non-empty — Expand/Collapse All are meaningless otherwise. */
    grouped: boolean
    /** The format currently in force for the target column. */
    format: BossFormat
    /** The measure the config currently sorts by, and its direction. */
    sortedBy: { measure: string; direction: 'asc' | 'desc' } | null
    /** How the Rows area is drawn; null when the layout carries a mode this menu does not offer. */
    rowLayout: BossRowLayout | null
    /**
     * Whether the column the menu was opened over is in the frozen block, or null when pinning does
     * not apply to it — empty space, or a derived column, which can never be a row field.
     */
    pinned: boolean | null
    onPin: (pinned: boolean) => void
    /** Whether the target column is a row field whose groups the side-by-side grid can open and close. */
    collapsible: boolean
    /** Open (Expand All) or close (Collapse All) every visible group of a row field. Side by side only. */
    onFieldAll: (field: string, open: boolean) => void
    /**
     * Whether the target row field's subtotal rows are shown, or null when the question does not
     * arise — the target is not a row field, or it is the last one and so has no subtotal rows.
     */
    totalsShown: boolean | null
    onShowTotals: (shown: boolean) => void
    /** Whether the grand TOTAL row is shown, or null when the target is not a row field. */
    grandTotalShown: boolean | null
    onShowGrandTotal: (shown: boolean) => void
    onExpandAll: () => void
    onCollapseAll: () => void
    onSortBy: (measure: string | null, direction: 'asc' | 'desc') => void
    onFormat: (format: BossFormat) => void
    onRowLayout: (layout: BossRowLayout) => void
    onClose: () => void
}

/**
 * One row of one menu level. Separators are rendered but skipped by the keyboard walk, and a
 * submenu carries its own children so every level can be rendered by the same component.
 */
type MenuNode =
    | { kind: 'action'; id: string; label: string; onSelect: () => void }
    /** One of a set: rendered as a radio item. */
    | { kind: 'choice'; id: string; label: string; checked: boolean; onSelect: () => void }
    /** On or off by itself: rendered as a checkbox item. */
    | { kind: 'toggle'; id: string; label: string; checked: boolean; onSelect: () => void }
    | { kind: 'submenu'; id: string; label: string; checked: boolean; items: MenuNode[] }
    | { kind: 'separator'; id: string }

/**
 * The legacy "Format To" captions (C8). `decimal` is Perspective's `number_format.style` name;
 * the menu says "Numeric" because that is the word the legacy grid used and the word the people
 * porting off it will look for.
 */
const FORMAT_CHOICES: { format: BossFormat; label: string }[] = [
    { format: 'auto', label: 'Auto' },
    { format: 'decimal', label: 'Numeric' },
    { format: 'currency', label: 'Currency' },
    { format: 'percent', label: 'Percent' },
]

/**
 * Row Layout. Not a legacy menu item — the legacy grid had no such choice because it only ever
 * drew row fields side by side, which is what "Side by side" restores and what an imported report
 * starts on. "Tree" is Perspective's own shape, and it is worth offering rather than hiding: it is
 * the only thing on this grid that produces a TOTAL row and a sub-total per group.
 */
const ROW_LAYOUT_CHOICES: { layout: BossRowLayout; label: string }[] = [
    { layout: 'flat', label: 'Side by side' },
    { layout: 'tree', label: 'Tree (with totals)' },
]

/** Distance a panel keeps from the viewport edge, matching `ContextMenu`. */
const EDGE_PAD = 8

// ── One level of the menu ─────────────────────────────────────────────

type MenuPanelProps = {
    items: MenuNode[]
    /** Names the level for assistive tech: "Grid actions", or the parent row's caption. */
    label: string
    /** The root sits at the click coordinate; a flyout hangs off the row that opened it. */
    placement: { kind: 'anchored'; x: number; y: number } | { kind: 'flyout' }
    /** Move DOM focus onto the first row — true for the root, and for a keyboard-opened flyout. */
    takeFocus: boolean
    /** Tear the whole menu down: a leaf was chosen, or Escape/Tab was pressed. */
    onDismiss: () => void
    /** ← inside a flyout: close this level and hand focus back to the row that opened it. */
    onCloseSelf: () => void
}

function MenuPanel({ items, label, placement, takeFocus, onDismiss, onCloseSelf }: MenuPanelProps): JSX.Element {
    const panelRef = useRef<HTMLDivElement | null>(null)
    const rowRefs = useRef<(HTMLButtonElement | null)[]>([])
    /**
     * The highlighted row, plus how it got there. Hovering must highlight without stealing DOM
     * focus from wherever the keyboard left it, so only a keyboard move is allowed to focus.
     */
    const [active, setActive] = useState<{ index: number; viaKeyboard: boolean }>({ index: -1, viaKeyboard: false })
    const [open, setOpen] = useState<{ id: string; viaKeyboard: boolean } | null>(null)
    const [anchor, setAnchor] = useState(() =>
        placement.kind === 'anchored' ? { left: placement.x, top: placement.y } : { left: 0, top: 0 })
    const [flyout, setFlyout] = useState<{ flipX: boolean; shiftY: number }>({ flipX: false, shiftY: 0 })

    /** Row indexes the keyboard can land on, in order — everything but the separators. */
    const steps = useMemo(
        () => items.map((node, index) => (node.kind === 'separator' ? -1 : index)).filter(index => index >= 0),
        [items],
    )
    const firstStep = steps.length > 0 ? steps[0] : -1

    /**
     * A panel with no children of its own may scroll when it is taller than the viewport; one that
     * opens flyouts may not, because any `overflow` other than `visible` on one axis clips the
     * other and would cut the flyout off at the panel's edge.
     */
    const scrollable = useMemo(() => items.every(node => node.kind !== 'submenu'), [items])

    /**
     * This level owns DOM focus unless a flyout was opened from the keyboard, in which case the
     * flyout has taken it. A hover-opened flyout leaves focus here, so ↑/↓ keep walking this level.
     */
    const ownsFocus = open === null || !open.viaKeyboard

    useEffect(() => {
        if (!takeFocus) return
        setActive({ index: firstStep, viaKeyboard: true })
    }, [takeFocus, firstStep])

    useEffect(() => {
        if (!ownsFocus || !active.viaKeyboard || active.index < 0) return
        rowRefs.current[active.index]?.focus()
    }, [active, ownsFocus])

    /**
     * Clamp the root to the viewport on all four edges, exactly as `ContextMenu` does. Mount-only:
     * `GridMenu` keys this panel on the coordinate, so a fresh right-click remounts it rather than
     * re-running this against an already-clamped position.
     */
    useLayoutEffect(() => {
        if (placement.kind !== 'anchored') return
        const panel = panelRef.current
        if (!panel) return
        const rect = panel.getBoundingClientRect()

        let left = placement.x
        let top = placement.y
        if (left + rect.width > window.innerWidth - EDGE_PAD) left = window.innerWidth - rect.width - EDGE_PAD
        if (left < EDGE_PAD) left = EDGE_PAD
        if (top + rect.height > window.innerHeight - EDGE_PAD) top = window.innerHeight - rect.height - EDGE_PAD
        if (top < EDGE_PAD) top = EDGE_PAD

        if (left !== placement.x || top !== placement.y) setAnchor({ left, top })
        // Deliberately empty: mount-only, for the reason above.
    }, [])

    /**
     * A flyout opens to the right of its row. If that runs off the screen it flips to the left,
     * but only when there is actually room there; if it runs off the bottom it slides up until it
     * fits. Measured once, before paint, against the un-adjusted position, so it cannot feed back
     * into itself.
     */
    useLayoutEffect(() => {
        if (placement.kind !== 'flyout') return
        const panel = panelRef.current
        if (!panel) return
        const rect = panel.getBoundingClientRect()
        const row = panel.parentElement?.getBoundingClientRect() ?? null

        const flipX = rect.right > window.innerWidth - EDGE_PAD
            && row !== null
            && rect.width <= row.left - EDGE_PAD
        const overflowY = rect.bottom - (window.innerHeight - EDGE_PAD)
        const shiftY = overflowY > 0 ? -Math.min(overflowY, Math.max(0, rect.top - EDGE_PAD)) : 0

        if (flipX || shiftY !== 0) setFlyout({ flipX, shiftY })
        // Deliberately empty: mount-only, for the reason above.
    }, [])

    const move = useCallback((delta: number) => {
        setActive(current => {
            if (steps.length === 0) return current
            const at = steps.indexOf(current.index)
            const next = at < 0
                ? (delta > 0 ? 0 : steps.length - 1)
                : (at + delta + steps.length) % steps.length
            return { index: steps[next], viaKeyboard: true }
        })
    }, [steps])

    const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        const node = active.index >= 0 ? items[active.index] : undefined

        // Every key handled here is stopped, so a flyout's keystroke is not processed a second
        // time by the panel it is rendered inside.
        const claim = () => {
            event.preventDefault()
            event.stopPropagation()
        }

        switch (event.key) {
            case 'ArrowDown':
                claim()
                move(1)
                break
            case 'ArrowUp':
                claim()
                move(-1)
                break
            case 'Home':
                claim()
                setActive({ index: firstStep, viaKeyboard: true })
                break
            case 'End':
                claim()
                setActive({ index: steps.length > 0 ? steps[steps.length - 1] : -1, viaKeyboard: true })
                break
            case 'ArrowRight':
                if (node && node.kind === 'submenu') {
                    claim()
                    setOpen({ id: node.id, viaKeyboard: true })
                }
                break
            case 'ArrowLeft':
                claim()
                // Closest level first: a flyout this row opened, otherwise this level itself. At
                // the root `onCloseSelf` is a no-op, so ← there does nothing.
                if (open !== null) setOpen(null)
                else onCloseSelf()
                break
            case 'Escape':
                claim()
                onDismiss()
                break
            case 'Tab':
                // Never let focus walk out of an open menu and leave it stranded over the grid.
                claim()
                onDismiss()
                break
            default:
                break
        }
        // Enter and Space are deliberately absent: the rows are <button>s, so the browser already
        // turns both into a click, and handling them here would fire every choice twice.
    }

    const style: CSSProperties = placement.kind === 'anchored'
        ? { left: anchor.left, top: anchor.top }
        : { top: flyout.shiftY }

    const panelClass = [
        'boss-gridmenu-panel',
        placement.kind === 'flyout' ? 'boss-gridmenu-flyout' : '',
        flyout.flipX ? 'boss-gridmenu-flip' : '',
        scrollable ? 'boss-gridmenu-scroll' : '',
    ].filter(part => part !== '').join(' ')

    return (
        <div
            ref={panelRef}
            className={panelClass}
            role="menu"
            aria-label={label}
            aria-orientation="vertical"
            style={style}
            onKeyDown={handleKeyDown}
            onContextMenu={event => {
                // A right-click on the menu is not a new right-click on the grid, and it must not
                // hand the browser's own menu back after the grid just suppressed it.
                event.preventDefault()
                event.stopPropagation()
            }}
        >
            {items.map((node, index) => {
                if (node.kind === 'separator') {
                    return <div key={node.id} className="boss-gridmenu-separator" role="separator" />
                }

                const isSubmenu = node.kind === 'submenu'
                const expanded = isSubmenu && open?.id === node.id
                const checked = node.kind === 'action' ? false : node.checked

                return (
                    <div key={node.id} className="boss-gridmenu-row">
                        <button
                            type="button"
                            ref={element => { rowRefs.current[index] = element }}
                            className={`boss-gridmenu-item${active.index === index ? ' active' : ''}`}
                            role={node.kind === 'choice' ? 'menuitemradio' : node.kind === 'toggle' ? 'menuitemcheckbox' : 'menuitem'}
                            aria-checked={node.kind === 'choice' || node.kind === 'toggle' ? node.checked : undefined}
                            aria-haspopup={isSubmenu ? 'menu' : undefined}
                            aria-expanded={isSubmenu ? expanded : undefined}
                            // Focus is moved programmatically along the level; Tab dismisses.
                            tabIndex={-1}
                            onMouseEnter={() => {
                                setActive({ index, viaKeyboard: false })
                                // Hover opens a submenu and closes a sibling's, which is what makes
                                // the menu feel like the legacy one without a click per level.
                                setOpen(node.kind === 'submenu' ? { id: node.id, viaKeyboard: false } : null)
                            }}
                            onClick={event => {
                                if (node.kind === 'submenu') {
                                    // detail === 0 means the click came from Enter/Space rather than
                                    // the mouse, and only then should focus follow into the flyout.
                                    const viaKeyboard = event.detail === 0
                                    setOpen(current =>
                                        current?.id === node.id && !viaKeyboard
                                            ? null
                                            : { id: node.id, viaKeyboard })
                                    return
                                }
                                node.onSelect()
                                onDismiss()
                            }}
                        >
                            <span className="boss-gridmenu-check" aria-hidden="true">{checked ? '✓' : ''}</span>
                            <span className="boss-gridmenu-label">{node.label}</span>
                            {isSubmenu && <span className="boss-gridmenu-arrow" aria-hidden="true">▸</span>}
                        </button>

                        {expanded && node.kind === 'submenu' && (
                            <MenuPanel
                                items={node.items}
                                label={node.label}
                                placement={{ kind: 'flyout' }}
                                takeFocus={open?.viaKeyboard === true}
                                onDismiss={onDismiss}
                                onCloseSelf={() => {
                                    setOpen(null)
                                    // A fresh object so the focus effect re-runs and the row that
                                    // opened the flyout gets focus back.
                                    setActive({ index, viaKeyboard: true })
                                }}
                            />
                        )}
                    </div>
                )
            })}
        </div>
    )
}

// ── The menu ──────────────────────────────────────────────────────────

export function GridMenu({
    target,
    position,
    measures,
    grouped,
    format,
    sortedBy,
    rowLayout,
    pinned,
    onPin,
    collapsible,
    onFieldAll,
    totalsShown,
    onShowTotals,
    grandTotalShown,
    onShowGrandTotal,
    onExpandAll,
    onCollapseAll,
    onSortBy,
    onFormat,
    onRowLayout,
    onClose,
}: GridMenuProps): JSX.Element | null {
    const portalRef = useRef<HTMLDivElement | null>(null)

    const nodes = useMemo<MenuNode[]>(() => {
        const onRowArea = grouped && target.kind === 'rowHeader'

        // C5. Depth only means something once there is a row tree to walk, and only the tree
        // column can be expanded — Perspective's split columns are always fully expanded (§3 C10).
        //
        // Side by side has no tree column to walk, but it has a group tree behind it
        // (lib/bossRowCollapse), and the same two words mean what the desktop grid's did: Expand All
        // opens every visible group of THIS field one level, Collapse All closes them — the other
        // fields, and any parent closed by hand, are left as they are. Both act on a field, so they
        // are offered only over a row field.
        const expandGroup: MenuNode[] = []
        if (onRowArea && rowLayout === 'tree') {
            expandGroup.push(
                { kind: 'action', id: 'expand-all', label: 'Expand All', onSelect: onExpandAll },
                { kind: 'action', id: 'collapse-all', label: 'Collapse All', onSelect: onCollapseAll },
            )
        } else if (onRowArea && rowLayout === 'flat' && collapsible && target.column !== null) {
            const column = target.column
            expandGroup.push(
                { kind: 'action', id: 'expand-all', label: 'Expand All', onSelect: () => onFieldAll(column, true) },
                { kind: 'action', id: 'collapse-all', label: 'Collapse All', onSelect: () => onFieldAll(column, false) },
            )
            // C6, the legacy per-field "Show Totals": the subtotal row the grid draws for every
            // group of this field. Withheld on the last row field, which has no such row.
            if (totalsShown !== null) {
                expandGroup.push({
                    kind: 'toggle',
                    id: 'show-totals',
                    label: 'Show Totals',
                    checked: totalsShown,
                    onSelect: () => onShowTotals(!totalsShown),
                })
            }
            // C12, the grand TOTAL at the foot of the grid. One switch for the whole grid, offered
            // from every row field so it is where a user reaching for a total will look.
            if (grandTotalShown !== null) {
                expandGroup.push({
                    kind: 'toggle',
                    id: 'show-grand-total',
                    label: 'Show Grand Total',
                    checked: grandTotalShown,
                    onSelect: () => onShowGrandTotal(!grandTotalShown),
                })
            }
        }

        // Row Layout sits with them because it is the same subject — how the row area is drawn —
        // and it is offered whenever there IS a row area, including in side-by-side mode where
        // Expand/Collapse are not, since it is the way back to the tree.
        if (onRowArea) {
            expandGroup.push({
                kind: 'submenu',
                id: 'row-layout',
                label: 'Row Layout',
                // Ticked for 'flat' only: the tree is Perspective's default and an unticked parent
                // reads as "nothing has been changed here", which is true.
                checked: rowLayout === 'flat',
                items: ROW_LAYOUT_CHOICES.map<MenuNode>(choice => ({
                    kind: 'choice',
                    id: `row-layout-${choice.layout}`,
                    label: choice.label,
                    checked: rowLayout === choice.layout,
                    onSelect: () => onRowLayout(choice.layout),
                })),
            })
        }

        // Pinning is about ONE column, so it goes at the top where the target is still in mind,
        // above the items that act on the whole grid. Withheld where it means nothing: empty space,
        // and a derived column, which the engine cannot group on at all.
        if (pinned !== null) {
            expandGroup.unshift({
                kind: 'action',
                id: 'pin',
                label: pinned ? 'Unpin column' : 'Pin column',
                onSelect: () => onPin(!pinned),
            })
        }

        const rest: MenuNode[] = []

        // C7. Legacy set `SortBySummaryInfo` on the field the menu was invoked from, so level 1
        // could sort by RETAIL while level 2 stayed alphabetical. Perspective orders by the
        // aggregate at EVERY level; there is no per-level route, so this one item changes the
        // whole grid. The divergence is stated here and in `setSortBySummary`.
        if ((target.kind === 'rowHeader' || target.kind === 'columnHeader') && measures.length > 0) {
            rest.push({
                kind: 'submenu',
                id: 'sort-by',
                label: 'Sort By',
                checked: sortedBy !== null,
                items: [
                    {
                        kind: 'choice',
                        id: 'sort-default',
                        label: '[Default sorting]',
                        checked: sortedBy === null,
                        // Direction is meaningless when the sort is being cleared, but the callback
                        // takes one; 'asc' is the inert half of the pair.
                        onSelect: () => onSortBy(null, 'asc'),
                    },
                    { kind: 'separator', id: 'sort-separator' },
                    ...measures.map<MenuNode>(measure => ({
                        kind: 'submenu',
                        id: `sort-${measure}`,
                        label: measure,
                        checked: sortedBy !== null && sortedBy.measure === measure,
                        items: [
                            {
                                kind: 'choice',
                                id: `sort-${measure}-asc`,
                                label: 'Ascending',
                                checked: sortedBy !== null && sortedBy.measure === measure && sortedBy.direction === 'asc',
                                onSelect: () => onSortBy(measure, 'asc'),
                            },
                            {
                                kind: 'choice',
                                id: `sort-${measure}-desc`,
                                label: 'Descending',
                                checked: sortedBy !== null && sortedBy.measure === measure && sortedBy.direction === 'desc',
                                onSelect: () => onSortBy(measure, 'desc'),
                            },
                        ],
                    })),
                ],
            })
        }

        // C8 on a measure, S3 on a row label. S3 is UNVERIFIED: whether a `columns_config` entry
        // on a group_by field reaches the tree labels has not been confirmed against a running
        // grid, so on a row header this item may format nothing. It is offered because the legacy
        // menu offered it there; if it proves inert, drop 'rowHeader' from this test rather than
        // leaving a control that lies.
        //
        // A row header the hit test could not resolve to a field carries no column name, so there
        // is nothing to write a format onto — the item is withheld instead of no-opping.
        const formattable = target.kind === 'cell'
            || target.kind === 'columnHeader'
            || (target.kind === 'rowHeader' && target.column !== null)

        if (formattable) {
            rest.push({
                kind: 'submenu',
                id: 'format-to',
                label: 'Format To',
                // The parent is ticked whenever an override is in force, so the current state is
                // readable without opening the flyout.
                checked: format !== 'auto',
                items: FORMAT_CHOICES.map<MenuNode>(choice => ({
                    kind: 'choice',
                    id: `format-${choice.format}`,
                    label: choice.label,
                    checked: format === choice.format,
                    onSelect: () => onFormat(choice.format),
                })),
            })
        }

        if (expandGroup.length > 0 && rest.length > 0) {
            return [...expandGroup, { kind: 'separator', id: 'group-separator' }, ...rest]
        }
        return [...expandGroup, ...rest]
    }, [
        target, grouped, measures, sortedBy, format, rowLayout, pinned, collapsible, totalsShown, grandTotalShown,
        onExpandAll, onCollapseAll, onSortBy, onFormat, onRowLayout, onPin, onFieldAll, onShowTotals, onShowGrandTotal,
    ])

    // Right-clicking empty space, or a target nothing on this menu applies to, must not leave an
    // empty box on screen — nor leave the page believing a menu is open with nothing to dismiss.
    const empty = nodes.length === 0
    useEffect(() => {
        if (empty) onClose()
    }, [empty, onClose])

    // Dismissal, borrowed from ContextMenu: an outside mousedown, Escape, or any scroll underneath
    // — the menu is pinned to a viewport coordinate, so a scrolled grid puts it over the wrong cell.
    useEffect(() => {
        const handleMouseDown = (event: MouseEvent) => {
            if (portalRef.current && !portalRef.current.contains(event.target as Node)) onClose()
        }
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        const handleScroll = () => onClose()

        document.addEventListener('mousedown', handleMouseDown)
        document.addEventListener('keydown', handleKey)
        window.addEventListener('scroll', handleScroll, true)
        return () => {
            document.removeEventListener('mousedown', handleMouseDown)
            document.removeEventListener('keydown', handleKey)
            window.removeEventListener('scroll', handleScroll, true)
        }
    }, [onClose])

    if (empty) return null

    return createPortal(
        // display: contents — the wrapper exists only so one ref can answer "was that click inside
        // the menu?" for the root and every open flyout at once.
        <div ref={portalRef} className="boss-gridmenu-portal">
            <MenuPanel
                // A new right-click remounts the menu, which resets the open flyout, the highlight
                // and the clamped position in one go.
                key={`${position.x}:${position.y}`}
                items={nodes}
                label="Grid actions"
                placement={{ kind: 'anchored', x: position.x, y: position.y }}
                takeFocus
                onDismiss={onClose}
                onCloseSelf={() => { /* ← at the root level has nowhere to go. */ }}
            />
        </div>,
        document.body,
    )
}

export default GridMenu
